import { ChevronLeft } from "lucide-react";
import { useState } from "react";

import { EditButton } from "./EditButton";

type MBCTitleBarProps = {
  actions?: React.ReactNode;
  brand?: { logoUrl?: null | string; name: string } | null;
  designTypeName?: string;
  onBack?: () => void;
  onTitleSave?: (value: string) => void;
  title: string;
};

type TitleInputProps = {
  onDone: () => void;
  onSave: (value: string) => void;
  value: string;
};

function TitleInput({ onDone, onSave, value }: TitleInputProps) {
  const [draft, setDraft] = useState(value);

  const commit = () => {
    const next = draft.trim();

    if (next && next !== value.trim()) onSave(next);
    onDone();
  };

  return (
    <input
      aria-label="Title"
      autoFocus
      className="min-w-0 flex-1 rounded-md border border-border-brand bg-bg-primary px-2 py-1 text-xl font-bold tracking-tight text-text-primary outline-hidden"
      onBlur={commit}
      onChange={(event) => setDraft(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === "Enter") commit();
        if (event.key === "Escape") onDone();
      }}
      value={draft}
    />
  );
}

export function MBCTitleBar({
  actions,
  brand,
  designTypeName,
  onBack,
  onTitleSave,
  title,
}: MBCTitleBarProps) {
  const [isEditing, setIsEditing] = useState(false);

  return (
    <header className="flex h-[72px] shrink-0 items-center gap-lg border-b border-border-secondary bg-bg-primary px-md lg:px-3xl">
      <button
        aria-label="Go back"
        className="flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-[10px] border border-border-primary bg-bg-primary text-text-secondary outline-hidden transition-colors hover:bg-bg-primary-hover"
        onClick={onBack}
        type="button"
      >
        <ChevronLeft className="size-[18px]" />
      </button>

      <div className="flex min-w-0 flex-1 items-center gap-3.5">
        {isEditing ? (
          <TitleInput
            onDone={() => setIsEditing(false)}
            onSave={(value) => onTitleSave?.(value)}
            value={title}
          />
        ) : (
          <h1 className="truncate text-xl font-bold tracking-tight text-text-primary">
            {title}
          </h1>
        )}
        {designTypeName ? (
          <span className="hidden whitespace-nowrap rounded-full border border-border-primary px-3 py-1 text-[13px] font-semibold text-text-secondary sm:inline">
            {designTypeName}
          </span>
        ) : null}
        {brand ? (
          <>
            <div className="hidden h-5 w-px shrink-0 bg-border-primary md:block" />
            <div className="hidden items-center gap-2 whitespace-nowrap md:flex">
              <span className="text-[13px] text-text-tertiary">Brand</span>
              {brand.logoUrl ? (
                <img
                  alt=""
                  className="size-5 shrink-0 rounded-[5px] object-cover"
                  src={brand.logoUrl}
                />
              ) : (
                <div
                  aria-hidden="true"
                  className="flex size-5 shrink-0 items-center justify-center rounded-[5px] bg-utility-gray-900 text-[11px] font-extrabold text-text-white"
                >
                  {brand.name.charAt(0).toUpperCase()}
                </div>
              )}
              <span className="text-sm font-semibold text-text-secondary">
                {brand.name}
              </span>
            </div>
          </>
        ) : null}
      </div>

      {onTitleSave && !isEditing ? (
        <EditButton label="title" onPress={() => setIsEditing(true)} />
      ) : null}
      {actions}
    </header>
  );
}
